import React, { useEffect, useMemo, useRef, useState } from 'react';

import { MOUSE_DOWN_EVENT } from 'constants/events';
import { debounce, isEmpty, map, min } from 'lodash';
import { useStandardsList } from 'sdk';

import { ReactComponent as SearchIcon } from 'pages/Teachers/Assignments/CreateMath/assets/search-icon.svg';
import { ReactComponent as StandardIcon } from 'pages/Teachers/Assignments/CreateMath/assets/standard-icon.svg';
import { getOptionLabel } from 'utils/assignments';
import { buildFilters } from 'utils/filters';

import styles from './styles.module.scss';

const StandardSelector = ({ selectedStandards, setSelectedStandards }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef();

  const addStandard = ({ standard }) => {
    if (!selectedStandards.find((s) => s.id === standard.id)) {
      setSelectedStandards((prev) => [...prev, standard]);
    }
    setSearchTerm('');
    setIsOpen(false);
  };

  const debouncedSetStandardSearchValue = debounce(
    (event) => setSearchParams(event.target.value),
    300
  );

  const params = useMemo(
    () =>
      buildFilters({
        search: searchParams,
        'exclude_standards[]': [...map(selectedStandards, 'id')],
        math_standards: true,
        retrieve_root_standards: false
      }),
    [searchParams, selectedStandards]
  );

  const removeStandard = ({ id }) => {
    setSelectedStandards((prev) => prev.filter((s) => s.id !== id));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener(MOUSE_DOWN_EVENT, handleClickOutside);
    return () =>
      document.removeEventListener(MOUSE_DOWN_EVENT, handleClickOutside);
  }, []);

  const { data, loading: isLoading } = useStandardsList({
    params: params
  });

  const minLevel = useMemo(() => min(map(data, 'level')) || 0, [data]);

  return (
    <div className={styles.fieldSection} ref={containerRef}>
      <div className={styles.fieldLabel}>
        <StandardIcon /> <span>Learning Standards</span>
      </div>

      <div className={styles.searchContainer}>
        <div className={styles.inputWrapper}>
          <input
            type="text"
            className={styles.inputField}
            placeholder="Search and add standards..."
            value={searchTerm}
            onFocus={() => setIsOpen(true)}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              debouncedSetStandardSearchValue(e);
            }}
          />
          <SearchIcon className={styles.searchIcon} />
        </div>

        {isOpen && !isEmpty(searchTerm) && (
          <div className={styles.dropdown}>
            {isLoading ? (
              <div className={styles.message}>Loading...</div>
            ) : !isEmpty(data) ? (
              map(data?.results, (standard) => {
                const paddingLeft = (standard.level - minLevel) * 16 + 12;
                return (
                  <div
                    key={standard.id}
                    className={styles.option}
                    style={{ paddingLeft: `${paddingLeft}px` }}
                    onClick={() => addStandard({ standard: standard })}
                  >
                    <span className={styles.name}>
                      {getOptionLabel({ option: standard })}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className={styles.message}>No matches found</div>
            )}
          </div>
        )}
      </div>

      {!isEmpty(selectedStandards) && (
        <div className={styles.chipsContainer}>
          {selectedStandards.map((standard) => (
            <div key={standard.id} className={styles.standardChip}>
              <span className={styles.chipText}>{standard.code}</span>
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => removeStandard({ id: standard.id })}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StandardSelector;
